import { normalizeAreaName } from "@a2z/text";
import type { AdminAreaSuggestionView, AdminAreaView } from "@/lib/api/types";

// Pass A's suggestion list response doesn't carry enough to flag "this looks
// like an area we already have" server-side (it only joins the governorate
// name, not a similarity check against the areas table). Cheaper to do the
// comparison client-side against the areas the admin page already fetches,
// than to ask Pass A to reopen the frozen contract for a nice-to-have hint.
// Both sides MUST go through the same normalizer. The suggestion's
// `normalizedName` is produced by the backend with `normalizeAreaName`
// (folds أ/إ/آ→ا, ى→ي, ة→ه, strips diacritics/tatweel); comparing it
// against a merely trim+lowercased catalog name meant "مدينه نصر" never
// matched catalog "مدينة نصر" — precisely the spelling variants this hint
// exists to catch. The unique key doesn't save us either, since `nameAr`
// genuinely differs, so the admin approved duplicates with no warning.
function normalize(value: string): string {
  return normalizeAreaName(value);
}

// Loose match: exact (case-insensitive) name, or one string containing the
// other — catches "6 اكتوبر" vs "السادس من اكتوبر"-style near-duplicates
// without pulling in a fuzzy-matching library for a hint that the admin
// still has to eyeball before approving.
export function looksLikeDuplicate(
  suggestion: Pick<AdminAreaSuggestionView, "governorateId" | "rawName" | "normalizedName">,
  existingAreas: AdminAreaView[],
): boolean {
  const candidates = [
    normalize(suggestion.normalizedName),
    normalize(suggestion.rawName),
  ].filter((c) => c.length > 0);
  if (candidates.length === 0) return false;

  for (const area of existingAreas) {
    if (area.governorateId !== suggestion.governorateId) continue;
    const names = [normalize(area.nameAr), normalize(area.nameEn ?? "")].filter(
      (n) => n.length > 0,
    );
    for (const name of names) {
      for (const candidate of candidates) {
        if (name === candidate || name.includes(candidate) || candidate.includes(name)) {
          return true;
        }
      }
    }
  }
  return false;
}
